import { ReactNode, useState } from 'react';
import { Lock } from 'lucide-react';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import UpgradePrompt from './UpgradePrompt';

interface FeatureGateProps {
  feature: string;
  children: ReactNode;
  fallback?: ReactNode;
  mode?: 'hide' | 'lock' | 'disable';
  showUpgradePrompt?: boolean;
}

/**
 * Feature gate component that controls access to features based on subscription plan
 *
 * Modes:
 * - 'hide': Completely hide the feature if not available (default for nav items)
 * - 'lock': Show the feature with a lock overlay and upgrade prompt
 * - 'disable': Show the feature but make it non-interactive with visual indication
 */
export default function FeatureGate({
  feature,
  children,
  fallback,
  mode = 'lock',
  showUpgradePrompt = true,
}: FeatureGateProps) {
  const { hasFeature } = useFeatureAccess();
  const [showPrompt, setShowPrompt] = useState(false);
  const hasAccess = hasFeature(feature);

  // If user has access, render children normally
  if (hasAccess) {
    return <>{children}</>;
  }

  // Mode: hide - Don't render anything
  if (mode === 'hide') {
    return fallback ? <>{fallback}</> : null;
  }

  // Mode: disable - Show content but make it non-interactive
  if (mode === 'disable') {
    return (
      <div className="relative">
        <div className="pointer-events-none opacity-50">{children}</div>
        {showUpgradePrompt && (
          <UpgradePrompt
            isOpen={showPrompt}
            onClose={() => setShowPrompt(false)}
            feature={feature}
          />
        )}
      </div>
    );
  }

  // Mode: lock - Show lock overlay with upgrade prompt
  return (
    <>
      <div className="relative">
        <div className="pointer-events-none opacity-40 blur-sm">{children}</div>
        <div
          className="absolute inset-0 flex cursor-pointer items-center justify-center bg-gray-50 bg-opacity-90"
          onClick={() => showUpgradePrompt && setShowPrompt(true)}
        >
          <div className="rounded-lg bg-white p-6 text-center shadow-lg">
            <Lock className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm font-medium text-gray-900">Feature Locked</p>
            <p className="mt-1 text-xs text-gray-600">
              Upgrade to access this feature
            </p>
            {showUpgradePrompt && (
              <button
                className="mt-3 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                onClick={() => setShowPrompt(true)}
              >
                Contact Sales
              </button>
            )}
          </div>
        </div>
      </div>

      {showUpgradePrompt && (
        <UpgradePrompt
          isOpen={showPrompt}
          onClose={() => setShowPrompt(false)}
          feature={feature}
        />
      )}
    </>
  );
}
